import { Box, Flex, Divider, Tag, Text, Button, Image } from "@chakra-ui/react";
import { useState } from "react";
import CardPerson from "components/Cards/CardPerson";
import CardDefault from "components/Cards/CardDefault";
import { TipoAutorizacaoPortariaEnum } from "features/portaria/enums";
import moment from "moment";
import { AutorizacaoItem } from "features/portaria/typings";
import useConfirmarAutorizacaoPortaria from "core/features/portaria/hooks/useConfirmarAutorizacaoPortaria";
import ModalDetalhePortaria from "./../ModalDetalhePortaria";
import partyPopper from "assets/images/svgs/party-popper.svg";
import colors from "core/resources/theme/colors";
import { formatPhone } from "infra/helpers/number";
import { showConfirm } from "infra/helpers/alerta";
import { StatusAutorizacaoPortaria } from "core/features/portaria/typings";
import ModalConfirmarPortaria from "../ModalConfirmarPortaria/index";

interface CardPortariaProps {
  refetch?: () => any;
  item: AutorizacaoItem;
  onShowListaConvidadosPortaria?: (
    data:
      | {
          portariaId: string;
          eventoId: string;
        }
      | undefined
  ) => void;
  onConfirmar?: (id?: string) => any;
  onConfirmarDadosPortaria?: (id: string) => any;
  onShowOutrasPessoas?: (
    data:
      | {
          portariaId: string;
          eventoId: string;
        }
      | undefined
  ) => void;
}

function CardPortaria({
  refetch,
  item,
  onShowListaConvidadosPortaria,
  onConfirmarDadosPortaria,
  onShowOutrasPessoas,
}: CardPortariaProps) {
  const [openModalConfirmar, setOpenModalConfirmar] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const { mutate: confirmar, isLoading: isLoadingConfirmar } =
    useConfirmarAutorizacaoPortaria(
      () => {
        refetch?.();
      },
      () => {}
    );

  const { horaEvento: hour } = item;

  const _dateStartPeriod = item.dataInicio
    ? moment(item.dataInicio).format("DD/MM/YYYY")
    : undefined;

  const _dateEndPeriod = item.dataFim
    ? moment(item.dataFim).format("DD/MM/YYYY")
    : undefined;

  const isRecurrent =
    _dateStartPeriod && _dateEndPeriod && _dateStartPeriod !== _dateEndPeriod;

  const isFuturePortaria = moment(item.dataInicio).isAfter(moment(), "day");

  const isPastPortaria = moment(item.dataFim).isBefore(
    moment().subtract(1, "day"),
    "day"
  );

  let displayDate = "";

  if (isRecurrent) {
    displayDate = `${moment(item.dataInicio).format("DD/MM/YYYY")} - ${moment(
      item.dataFim
    ).format("DD/MM/YYYY")}`;

    if (hour && hour !== "00:00:00") {
      displayDate += ` ás ${hour.slice(0, 5)}`;
    }
  } else {
    displayDate = moment(
      moment(item.dataInicio).format("YYYY-MM-DD") + "T" + hour
    ).format(`DD/MM/YYYY${hour !== "00:00:00" ? " [às] HH:mm" : ""}`);
  }

  const color = getColor(item.tipoAutorizacao.id);
  const isListaConvidados =
    item.tipoAutorizacao.id === TipoAutorizacaoPortariaEnum.ListaConvidados;

  const handleConfirmar = async () => {
    if (!isRecurrent && moment(item.dataInicio).diff(moment(), "day") < 0) {
      const { isConfirmed } = await showConfirm(
        "Esta mensagem é autorização do dia anterior. Deseja realmente confirmar esta entrada?"
      );

      if (isConfirmed) {
        confirmar({ id: item.id });
      }
    } else {
      const { isConfirmed } = await showConfirm(
        "Deseja realmente confirmar essa entrada?"
      );
      if (isConfirmed) {
        confirmar({ id: item.id });
      }
    }
  };

  return (
    <Box>
      <CardDefault
        mb={6}
        px={3}
        py={3}
        bg={openModalConfirmar ? "green.50" : "white"}
        borderLeft="10px solid"
        borderLeftColor={color}
        cursor="pointer"
        onClick={() => {
          if (isListaConvidados) {
            onShowListaConvidadosPortaria?.({
              eventoId: item.eventoId,
              portariaId: item.id,
            });
          } else {
            setOpenModal(true);
          }
        }}
        gridGap="16px"
      >
        <Flex gridGap="16px">
          {item.pessoa ? (
            <Flex>
              <CardPerson
                avatarUrl={item.pessoa?.imagemUrl}
                nome={item.pessoa?.nome}
                subtitle={formatCardUnidade(
                  item.unidade.numero,
                  item.unidade.blocoQuadra?.nome
                )}
                isPeriod={false}
              />
            </Flex>
          ) : (
            <Flex align="center" gap={2}>
              <Flex
                p="3px"
                border={`solid 1px ${colors.darkGray}`}
                rounded="full"
              >
                <Flex
                  w="50px"
                  h="50px"
                  align="center"
                  justify="center"
                  bg={colors.lightGray}
                  rounded="full"
                >
                  <Image src={partyPopper} w="100%" h="30px" />
                </Flex>
              </Flex>
              <Flex flexDir="column">
                <Text fontSize="20px" fontWeight="bold">
                  {item.tipoAutorizacao.nome}
                </Text>
                <Text>
                  {formatCardUnidade(
                    item.unidade.numero,
                    item.unidade.blocoQuadra?.nome
                  )}
                </Text>
              </Flex>
            </Flex>
          )}

          <Flex
            flex={1}
            alignItems={"start"}
            gridGap="8px"
            mt={2}
            flexWrap="wrap"
          >
            {item.pessoa ? (
              <Tag bg={color} color="#000">
                {item.tipoAutorizacao.nome}
              </Tag>
            ) : null}

            {item.quantidadePessoas ? (
              <Tag color="gray.500">
                {item.quantidadePessoas} pessoa
                {item.quantidadePessoas === 1 ? "" : "s"}
              </Tag>
            ) : null}

            <Tag color="gray.500">{displayDate}</Tag>
          </Flex>
          <Flex gap={4}>
            {item.status === StatusAutorizacaoPortaria.Confirmado ? (
              <Tag color="green.500" bg="white" h="32px">
                {isRecurrent ? "Confirmado hoje" : "Confirmado"}
              </Tag>
            ) : null}

            {!isListaConvidados && item.quantidadePessoas > 1 ? (
              <Button
                onClick={(ev) => {
                  ev.stopPropagation();
                  onShowOutrasPessoas?.({
                    eventoId: item.eventoId,
                    portariaId: item.id,
                  });
                }}
                size="sm"
                shadow="lg"
                colorScheme="facebook"
                type="button"
              >
                Ver outras entradas
              </Button>
            ) : null}

            {!isListaConvidados && !isPastPortaria && !isFuturePortaria ? (
              <Button
                onClick={(ev) => {
                  ev.stopPropagation();
                  onConfirmarDadosPortaria?.(item.id);
                }}
                size="sm"
                isLoading={isLoadingConfirmar}
                shadow="lg"
                colorScheme="facebook"
                type="button"
              >
                Complementar cadastro
              </Button>
            ) : null}

            {isListaConvidados ? (
              <Button
                onClick={(ev) => {
                  ev.stopPropagation();
                  onShowListaConvidadosPortaria?.({
                    eventoId: item.eventoId!,
                    portariaId: item.id!,
                  });
                }}
                size="sm"
                isLoading={isLoadingConfirmar}
                shadow="lg"
                colorScheme="facebook"
                type="button"
              >
                Ver lista
              </Button>
            ) : !isPastPortaria && !isFuturePortaria ? (
              <Button
                onClick={(ev) => {
                  ev.stopPropagation();
                  handleConfirmar();
                }}
                size="sm"
                isLoading={isLoadingConfirmar}
                shadow="lg"
                colorScheme="green"
                type="button"
              >
                Confirmar
              </Button>
            ) : null}
          </Flex>
        </Flex>

        <Divider />

        <Flex>
          {item?.pessoa?.documento ? (
            <Flex flex={0.5} flexDir="column">
              <Text color="gray.400" fontSize="12px">
                Documento
              </Text>
              <Text color="gray.800">{item?.pessoa?.documento ?? ""}</Text>
            </Flex>
          ) : null}

          {item?.pessoa?.cnh ? (
            <Flex flex={0.5} flexDir="column">
              <Text color="gray.400" fontSize="12px">
                CNH
              </Text>
              <Text color="gray.800">{item?.pessoa?.cnh ?? ""}</Text>
            </Flex>
          ) : null}

          <Flex flex={1.5} flexDir="column">
            <Text color="gray.400" fontSize="12px">
              Observação
            </Text>
            <Text color="gray.800">{item.observacao ?? ""}</Text>
          </Flex>

          <Flex flex={1}>
            <Flex flex={1} flexDir="column">
              <Text color="gray.400" fontSize="12px">
                Nome do solicitante
              </Text>
              <Text color="gray.800">{item.morador.nome ?? ""}</Text>
            </Flex>
          </Flex>

          <Flex flex={1}>
            <Flex flex={1} flexDir="column">
              <Text color="gray.400" fontSize="12px">
                Telefone do solicitante
              </Text>
              <Text color="gray.800">
                {item.morador.telefone
                  ? formatPhone(item.morador.telefone)
                  : ""}
              </Text>
            </Flex>
          </Flex>
          <Flex flex={0.5}>
            <Flex flex={1} flexDir="column">
              <Text color="gray.400" fontSize="12px">
                Data de criação
              </Text>
              <Text color="gray.800">
                {item.createdAt
                  ? moment(item.createdAt).format("DD/MM/YYYY [às] HH:mm")
                  : ""}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </CardDefault>

      <ModalDetalhePortaria
        visible={openModal}
        onClose={() => setOpenModal(false)}
        acompanhante={item.quantidadePessoas}
        autorizacaoId={item.id}
        pessoa={item.pessoa}
        observacao={item.observacao}
        nomeMorador={item.morador.nome}
        unidade={formatCardUnidade(
          item.unidade.numero,
          item.unidade.blocoQuadra?.nome
        )}
        date={displayDate}
        foto={item.pessoa?.imagemUrl ?? undefined}
        documento={item.documento ?? undefined}
        telefone={item.morador.telefone}
        status={item.status}
        dateStart={_dateStartPeriod}
        dateEnd={_dateEndPeriod}
        disableConfirmar={isPastPortaria || isFuturePortaria}
        onConfirm={handleConfirmar}
        cnh={item.pessoa?.cnh ?? undefined}
      />

      {openModalConfirmar ? (
        <ModalConfirmarPortaria
          visible={openModalConfirmar}
          onClose={() => setOpenModalConfirmar(false)}
          autorizacaoId={item.id}
          onConfirm={() => {
            handleConfirmar();
            setOpenModalConfirmar(false);
          }}
          onInputData={() => {
            setOpenModalConfirmar(false);
            setTimeout(() => {
              onConfirmarDadosPortaria?.(item.id);
            }, 100);
          }}
        />
      ) : null}
    </Box>
  );
}

const formatCardUnidade = (
  numero: string,
  blocoQuadra: string | null | undefined
) => {
  return numero + (blocoQuadra ? `, ${blocoQuadra}` : "");
};

export function getColor(tipo: TipoAutorizacaoPortariaEnum) {
  if (tipo === TipoAutorizacaoPortariaEnum.Delivery) {
    return "#4EC98A";
  }

  if (tipo === TipoAutorizacaoPortariaEnum.Encomenda) {
    return "#ECC94B";
  }

  if (tipo === TipoAutorizacaoPortariaEnum.FuncionarioDomestico) {
    return "#ABACF7";
  }

  if (tipo === TipoAutorizacaoPortariaEnum.PrestadorServico) {
    return "#ABACF7";
  }

  if (tipo === TipoAutorizacaoPortariaEnum.Transporte) {
    return "#ABACF7";
  }

  if (tipo === TipoAutorizacaoPortariaEnum.Visitante) {
    return "#22A6F0";
  }
}

export default CardPortaria;
